
import { saveAs } from 'file-saver';
import { BehaviorSubject, catchError, from, lastValueFrom, map, Observable, of, switchMap } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { DomainType, DomainUtility, FormResponse, FormResponseRequest, SaveFormResponse } from '@jms/model';
import { UrlHelper } from '@jms/utils/util.helper';
import { UtilService } from '@jms/utils/util.services';
import { PdfService } from '@workbuddy/services';

export const OVERRIDE_FORM_SERVICE = new InjectionToken<FormService>('OVERRIDE_FORM_SERVICE');

@Injectable({ providedIn: 'root' })
export class FormService {

    protected http = inject(HttpClient);
    protected pdfService = inject(PdfService);

    private readonly _lastResult$ = new BehaviorSubject<any[]>(null);
    public readonly lastResult$ = this._lastResult$.asObservable();
    protected isContractor = false;

    protected get api(): string {
        const api = this.isContractor
            ? 'https://__tenant__/api/form/response'
            : '/api/form/response';

        return api;
    }

    constructor() {
        const domain = UtilService.getLocation().hostName;
        this.isContractor = DomainUtility.getDomainType(domain) === DomainType.ContractorPortal;

    }

    public getFormResponse(formResponseRequest: string): Observable<FormResponse> {
        return this.http
            .get<FormResponse>(`${this.api}/${formResponseRequest}`)
            .pipe(
                map(res => {
                    return res;
                }),
                catchError(() => of(null))
            );
    }

    public saveFormResponse(formResponseRequest: SaveFormResponse): Observable<FormResponse> {
        return this.http
            .post<FormResponse>(`${this.api}/v2`, formResponseRequest)
            .pipe(
                map(res => {
                    return res;
                }),
                catchError(() => of(null))
            );
    }

    public saveFormResponseV2(formResponseRequest: SaveFormResponse): Observable<FormResponse> {
        return this.http
            .post<FormResponse>(`${this.api}/v2`, formResponseRequest)
            .pipe(
                map(res => {
                    return res;
                }),
                catchError(() => of(null))
            );
    }

    public getWorkComplianceFormResponse(responseId: string): Observable<FormResponse> {
        return this.http
            .get<FormResponse>(`${this.api}/workcompliance/${responseId}`)
            .pipe(
                map(res => {
                    return res;
                }),
                catchError(() => of(null))
            );
    }

    public getFormResponseById(responseId: string): Observable<FormResponse> {
        return this.http
            .get<FormResponse>(`${this.api}/selected/${responseId}`)
            .pipe(
                map(res => {
                    return res;
                }),
                catchError(() => of(null))
            );
    }

    public downloadForm(formResponse: FormResponse, tenant: string, domain: string,) {

        let pdfUrl = formResponse.pdf;

        if (!pdfUrl) {
            const request: FormResponseRequest = {
                rId: formResponse._id,
                tId: formResponse.template._id,
                eId: formResponse.entityId,
            };

            const formUrl = UrlHelper.serializeParams(request);
            const headerImage = formResponse?.template?.options?.print?.header?.image;

            const url = `https://${tenant}.${domain}/forms/print/${formUrl}`;

            pdfUrl = this.pdfService.generateFormPdfUrl(url);

            if (headerImage && headerImage.length > 0) {
                pdfUrl += `&headerUrl=${encodeURIComponent(headerImage[0].path)}`;
            }
        }

        return from(this.getPDFName(formResponse._id))
            .pipe(
                switchMap(name => {
                    return this.http
                        .get(pdfUrl, { responseType: 'blob' })
                        .pipe(
                            map(data => saveAs(data as Blob, name ?? `${formResponse?.template?.title}.pdf`)),
                        );
                }),
                catchError(error => of(null))
            );


    }

    public async getPDFName(responseId: string): Promise<string> {
        return await lastValueFrom(
            this.http.get<{ name: string }>(`${this.api}/name/${responseId}/`)
                .pipe(
                    map(x => {
                        return x?.name;
                    }),
                    catchError(err => {
                        return of(null);
                    })
                )
        );
    }

    public getTenantUrl(): Observable<string> {
        return of(null);
    }
}
